import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BackButton from '../../components/common/BackButton';

const ManageDoctors = () => {
    const { t } = useTranslation();
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingDoctor, setEditingDoctor] = useState(null);
    const [formData, setFormData] = useState({
        nameAr: '',
        nameEn: '',
        specialtyAr: '',
        specialtyEn: '',
        jobTitle: 'Doctor',
        image: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState('');

    useEffect(() => {
        fetchDoctors();
    }, []);

    const fetchDoctors = async () => {
        try {
            const res = await axios.get('https://hasanhaji.com/api/team');
            // Filter only doctors
            const doctorsOnly = res.data.data.teamMembers
                .filter(member => member.jobTitle === 'Doctor')
                .sort((a, b) => a.order - b.order);
            setDoctors(doctorsOnly);
            setLoading(false);
        } catch (err) {
            setError(t('admin.errorLoadingTeam'));
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const submitData = new FormData();
            submitData.append('nameAr', formData.nameAr);
            submitData.append('nameEn', formData.nameEn);
            submitData.append('specialtyAr', formData.specialtyAr);
            submitData.append('specialtyEn', formData.specialtyEn);
            submitData.append('jobTitle', 'Doctor'); // Always Doctor

            const imageFile = document.getElementById('imageUpload').files[0];
            if (imageFile) {
                submitData.append('image', imageFile);
            }

            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            };

            if (editingDoctor) {
                await axios.patch(`https://hasanhaji.com/api/team/${editingDoctor.id}`, submitData, config);
            } else {
                await axios.post('https://hasanhaji.com/api/team', submitData, config);
            }

            fetchDoctors();
            resetForm();
            setIsSubmitting(false);
        } catch (err) {
            console.error(err);
            setError(t('admin.errorSavingMember'));
            setIsSubmitting(false);
        }
    };

    const handleEdit = (doctor) => {
        setEditingDoctor(doctor);
        setFormData({
            nameAr: doctor.nameAr || doctor.name || '',
            nameEn: doctor.nameEn || doctor.name || '',
            specialtyAr: doctor.specialtyAr || doctor.specialty || '',
            specialtyEn: doctor.specialtyEn || doctor.specialty || '',
            jobTitle: 'Doctor',
            image: doctor.image || '',
        });
        setImagePreview(doctor.image || '');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('admin.confirmDeleteMember'))) {
            try {
                await axios.delete(`https://hasanhaji.com/api/team/${id}`);
                fetchDoctors();
            } catch (err) {
                setError(t('admin.errorDeletingMember'));
            }
        }
    };

    const handleMove = async (index, direction) => {
        if (loading || isSubmitting) return;

        const currentDoctor = doctors[index];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= doctors.length) return;

        const targetDoctor = doctors[targetIndex];

        setIsSubmitting(true);
        try {
            const currentOrder = currentDoctor.order;
            const targetOrder = targetDoctor.order;

            await Promise.all([
                axios.patch(`https://hasanhaji.com/api/team/${currentDoctor.id}`, { order: targetOrder }),
                axios.patch(`https://hasanhaji.com/api/team/${targetDoctor.id}`, { order: currentOrder })
            ]);

            await fetchDoctors();
        } catch (err) {
            console.error(err);
            setError(t('admin.errorReordering'));
            fetchDoctors();
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setEditingDoctor(null);
        setFormData({
            nameAr: '',
            nameEn: '',
            specialtyAr: '',
            specialtyEn: '',
            jobTitle: 'Doctor',
            image: '',
        });
        setImagePreview('');
        const imageUpload = document.getElementById('imageUpload');
        if (imageUpload) imageUpload.value = '';
    };

    return (
        <div className="container-fluid py-5 bg-light">
            <div className="container">
                <div className="d-flex justify-content-between align-items-center mb-5">
                    <div className="d-flex align-items-center gap-3">
                        <BackButton to="/admin" />
                        <h2 className="fw-bold text-primary mb-0">
                            <i className="fas fa-user-md me-2"></i> {t('admin.manageDoctors', 'إدارة الأطباء')}
                        </h2>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger shadow-sm border-0" role="alert">
                        <i className="fas fa-exclamation-circle me-2"></i> {error}
                    </div>
                )}

                <div className="row g-4">
                    {/* Form Section */}
                    <div className="col-lg-4">
                        <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px', zIndex: 1 }}>
                            <div className="card-header bg-white border-bottom-0 pt-4 px-4">
                                <h5 className="card-title fw-bold mb-0 text-dark">
                                    {editingDoctor ? t('admin.editDoctor', 'تعديل طبيب') : t('admin.addDoctor', 'إضافة طبيب')}
                                </h5>
                            </div>
                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit}>
                                    {/* Image Upload */}
                                    <div className="mb-4 text-center">
                                        <div className="position-relative d-inline-block">
                                            <div
                                                className="rounded-circle bg-light d-flex align-items-center justify-content-center border"
                                                style={{ width: '120px', height: '120px', overflow: 'hidden', margin: '0 auto' }}
                                            >
                                                {imagePreview ? (
                                                    <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <i className="fas fa-user-md fa-3x text-secondary"></i>
                                                )}
                                            </div>
                                            <label
                                                htmlFor="imageUpload"
                                                className="btn btn-sm btn-primary position-absolute bottom-0 end-0 rounded-circle shadow-sm"
                                                style={{ width: '32px', height: '32px', padding: '0', lineHeight: '32px' }}
                                            >
                                                <i className="fas fa-camera"></i>
                                            </label>
                                        </div>
                                        <input
                                            type="file"
                                            className="d-none"
                                            id="imageUpload"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                        />
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-6">
                                            <label className="form-label text-muted small fw-bold">{t('admin.nameAr')}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="nameAr"
                                                value={formData.nameAr}
                                                onChange={handleChange}
                                                required
                                                placeholder={t('admin.doctorNamePlaceholder', 'د. أحمد محمد')}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label text-muted small fw-bold">{t('admin.nameEn')}</label>
                                            <input
                                                type="text"
                                                className="form-control text-start"
                                                name="nameEn"
                                                value={formData.nameEn}
                                                onChange={handleChange}
                                                required
                                                placeholder={t('admin.doctorNameEnPlaceholder', 'Dr. Ahmed Mohamed')}
                                                dir="ltr"
                                            />
                                        </div>

                                        <div className="col-6">
                                            <label className="form-label text-muted small fw-bold">{t('admin.specialtyAr')}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="specialtyAr"
                                                value={formData.specialtyAr}
                                                onChange={handleChange}
                                                required
                                                placeholder={t('admin.specialtyPlaceholder', 'طب الأسرة')}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label text-muted small fw-bold">{t('admin.specialtyEn')}</label>
                                            <input
                                                type="text"
                                                className="form-control text-start"
                                                name="specialtyEn"
                                                value={formData.specialtyEn}
                                                onChange={handleChange}
                                                required
                                                placeholder={t('admin.specialtyEnPlaceholder', 'Family Medicine')}
                                                dir="ltr"
                                            />
                                        </div>
                                    </div>

                                    <div className="d-grid gap-2 mt-4">
                                        <button
                                            type="submit"
                                            className="btn btn-primary py-2 fw-bold"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="fas fa-save me-2"></i>}
                                            {editingDoctor ? t('admin.saveChanges') : t('admin.addDoctorBtn', 'إضافة طبيب')}
                                        </button>
                                        {editingDoctor && (
                                            <button
                                                type="button"
                                                className="btn btn-light text-danger py-2"
                                                onClick={resetForm}
                                            >
                                                {t('admin.cancel')}
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Doctors List */}
                    <div className="col-lg-8">
                        <div className="card border-0 shadow-sm bg-transparent">
                            <div className="card-header bg-transparent border-0 p-0 mb-3">
                                <h5 className="card-title fw-bold text-dark">{t('admin.doctorsList', 'قائمة الأطباء')}</h5>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : doctors.length === 0 ? (
                                <div className="alert alert-info border-0 shadow-sm" role="alert">
                                    <i className="fas fa-info-circle me-2"></i> {t('admin.noDoctors', 'لا يوجد أطباء حالياً')}
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {doctors.map((doctor, index) => (
                                        <div className="col-md-6" key={doctor.id}>
                                            <div className="card border-0 shadow-sm h-100 overflow-hidden member-card">
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-shrink-0">
                                                            <img
                                                                src={doctor.image || '/images/default-avatar.png'}
                                                                alt={doctor.name}
                                                                className="rounded-circle border"
                                                                style={{ width: '70px', height: '70px', objectFit: 'cover' }}
                                                                onError={(e) => { e.target.src = 'https://via.placeholder.com/70?text=Dr' }}
                                                            />
                                                        </div>
                                                        <div className="flex-grow-1 ms-3 me-3">
                                                            <div className="d-flex justify-content-between align-items-start">
                                                                <h6 className="fw-bold mb-1">{doctor.nameAr || doctor.name}</h6>
                                                                <span className="badge bg-primary rounded-pill">
                                                                    {t('admin.doctor')}
                                                                </span>
                                                            </div>
                                                            <p className="text-muted small mb-1">{doctor.nameEn}</p>
                                                            <div className="d-flex align-items-center text-secondary small">
                                                                <i className="fas fa-stethoscope me-1 text-primary opacity-50"></i>
                                                                <span>{doctor.specialtyAr || doctor.specialty}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-footer bg-light border-0 p-2 d-flex justify-content-between align-items-center">
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary border-0"
                                                            onClick={() => handleMove(index, 'up')}
                                                            disabled={index === 0 || isSubmitting}
                                                            title={t('admin.moveUp')}
                                                        >
                                                            <i className="fas fa-arrow-up"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary border-0"
                                                            onClick={() => handleMove(index, 'down')}
                                                            disabled={index === doctors.length - 1 || isSubmitting}
                                                            title={t('admin.moveDown')}
                                                        >
                                                            <i className="fas fa-arrow-down"></i>
                                                        </button>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            className="btn btn-sm btn-outline-primary border-0"
                                                            onClick={() => handleEdit(doctor)}
                                                            title={t('admin.edit')}
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger border-0"
                                                            onClick={() => handleDelete(doctor.id)}
                                                            title={t('admin.delete')}
                                                        >
                                                            <i className="fas fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
        .member-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .member-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
        }
      `}</style>
        </div>
    );
};

export default ManageDoctors;
